import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ShieldIcon, MenuIcon, XIcon, ChevronDownIcon } from '@/components/layout/icons';
import { ServicesMenu } from '@/components/layout/services-menu';
import { cn } from '@/lib/cn';
import { useGroupedServices, useAllServices } from '@shared/hooks/use-services';
import {
  SERVICE_CATEGORY_LABELS,
  type ServiceCategory,
  type ServiceSummary,
} from '@shared/types/api';

// Public top nav on the landing and 404. Clients tap "Get Started" (email OTP);
// professionals tap "Partner with Us" (associate signup). Each email can only
// be registered as one role — the OTP page handles the mismatch case.
const NAV = [
  { href: '#why', label: 'Why EcRegi' },
  { href: '#how', label: 'How it Works' },
  { href: '#benefits', label: 'Benefits' },
  { href: '#faq', label: 'FAQ' },
];

function MobileServicesMenu({ onClose }: { onClose: () => void }) {
  const [expanded, setExpanded] = useState(false);
  const [groupMode, setGroupMode] = useState<'authority' | 'category'>('authority');
  const [activeCat, setActiveCat] = useState<ServiceCategory | null>(null);
  const [activeAuthId, setActiveAuthId] = useState<string | null>(null);

  const { data } = useGroupedServices();
  const { data: allServices = [] } = useAllServices();

  const authorityGroups = useMemo(() => {
    const map = new Map<
      string,
      {
        id: string;
        name: string;
        code: string | null;
        image?: string | null;
        createdAt?: string | Date | null;
        items: ServiceSummary[];
      }
    >();

    for (const s of allServices) {
      if (!s.authority) continue;

      const authId = s.authority.id;
      const authName = s.authority.name;
      const authCode = s.authority.code ?? null;
      const authImage = s.authority.images?.[0] ?? null;
      const authCreatedAt = s.authority.createdAt ?? null;

      if (!map.has(authId)) {
        map.set(authId, {
          id: authId,
          name: authName,
          code: authCode,
          image: authImage,
          createdAt: authCreatedAt,
          items: [],
        });
      } else {
        const item = map.get(authId)!;
        if (!item.image && authImage) item.image = authImage;
        if (!item.createdAt && authCreatedAt) item.createdAt = authCreatedAt;
      }
      map.get(authId)!.items.push(s);
    }

    return Array.from(map.values()).sort((a, b) => {
      if (a.createdAt && b.createdAt) {
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      }
      return 0;
    });
  }, [allServices]);

  return (
    <div className="flex flex-col">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="flex items-center justify-between py-2 text-base font-semibold text-gray-800 hover:text-gray-950 transition-colors"
      >
        <span>Services</span>
        <ChevronDownIcon
          className={cn(
            'h-5 w-5 text-gray-500 transition-transform duration-200',
            expanded && 'rotate-180 text-gray-900',
          )}
        />
      </button>

      {expanded && (
        <div className="mt-2 pl-3 border-l-2 border-gray-200 flex flex-col gap-3 transition-all">
          {/* Segmented Control Toggle (By Authority vs By Category) */}
          <div className="flex rounded-lg bg-gray-100 p-1">
            <button
              type="button"
              onClick={() => setGroupMode('authority')}
              className={cn(
                'flex-1 rounded-md py-1.5 text-center text-xs font-semibold transition-all cursor-pointer',
                groupMode === 'authority'
                  ? 'bg-white text-brand-800 shadow-xs ring-1 ring-black/5'
                  : 'text-gray-600 hover:text-gray-900',
              )}
            >
              By Authority
            </button>
            <button
              type="button"
              onClick={() => setGroupMode('category')}
              className={cn(
                'flex-1 rounded-md py-1.5 text-center text-xs font-semibold transition-all cursor-pointer',
                groupMode === 'category'
                  ? 'bg-white text-brand-800 shadow-xs ring-1 ring-black/5'
                  : 'text-gray-600 hover:text-gray-900',
              )}
            >
              By Category
            </button>
          </div>

          {/* BY AUTHORITY VIEW */}
          {groupMode === 'authority' && (
            <div className="flex flex-col gap-1">
              {authorityGroups.length === 0 ? (
                <p className="py-2 text-xs text-gray-500">
                  No services registered under any government authority yet.
                </p>
              ) : (
                authorityGroups.map((authGroup) => {
                  const isActive = activeAuthId === authGroup.id;
                  return (
                    <div key={authGroup.id} className="flex flex-col">
                      <button
                        type="button"
                        onClick={() => setActiveAuthId(isActive ? null : authGroup.id)}
                        className={cn(
                          'flex items-center justify-between py-2 text-sm font-medium transition-colors text-left gap-2 cursor-pointer',
                          isActive
                            ? 'text-brand-700 font-semibold'
                            : 'text-gray-700 hover:text-gray-950',
                        )}
                      >
                        <div className="flex items-center gap-1.5 min-w-0 pr-1">
                          {authGroup.image ? (
                            <img
                              src={authGroup.image}
                              alt={authGroup.name}
                              className="h-4 w-4 rounded object-contain shrink-0"
                            />
                          ) : null}
                          <span className="truncate">{authGroup.name}</span>
                          {authGroup.code && (
                            <span className="rounded bg-brand-50 border border-brand-200 px-1.5 py-0.5 text-[10px] font-semibold text-brand-700 shrink-0">
                              {authGroup.code}
                            </span>
                          )}
                        </div>
                        <span className="flex items-center gap-1 text-xs text-gray-400 shrink-0">
                          <span className="tabular-nums">{authGroup.items.length}</span>
                          <ChevronDownIcon
                            className={cn(
                              'h-4 w-4 transition-transform duration-200',
                              isActive && 'rotate-180 text-brand-700',
                            )}
                          />
                        </span>
                      </button>

                      {isActive && (
                        <div className="pl-3 py-1.5 flex flex-col gap-1.5 border-l border-brand-200 bg-brand-50/30 rounded-r-md">
                          {authGroup.items.map((item) => (
                            <Link
                              key={item.id}
                              to={`/client/services/${item.id}`}
                              onClick={onClose}
                              className="py-1 text-xs font-medium text-gray-700 hover:text-brand-700 flex items-center justify-between gap-2"
                            >
                              <span className="truncate">{item.name}</span>
                              {item.authorityServiceCode ? (
                                <span className="text-[10px] font-mono text-emerald-800 bg-emerald-100/80 px-1.5 py-0.5 rounded shrink-0">
                                  {item.authorityServiceCode}
                                </span>
                              ) : null}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* BY CATEGORY VIEW */}
          {groupMode === 'category' && (
            <div className="flex flex-col gap-1">
              {(data?.groups ?? []).map((group) => {
                const isActive = activeCat === group.category;
                const totalItems = group.subGroups.reduce((n, sg) => n + sg.items.length, 0);
                if (totalItems === 0) return null;

                return (
                  <div key={group.category} className="flex flex-col">
                    <button
                      type="button"
                      onClick={() => setActiveCat(isActive ? null : group.category)}
                      className={cn(
                        'flex items-center justify-between py-2 text-sm font-medium transition-colors text-left cursor-pointer',
                        isActive
                          ? 'text-brand-700 font-semibold'
                          : 'text-gray-700 hover:text-gray-950',
                      )}
                    >
                      <span>{SERVICE_CATEGORY_LABELS[group.category]}</span>
                      <span className="flex items-center gap-1 text-xs text-gray-400">
                        <span className="tabular-nums">{totalItems}</span>
                        <ChevronDownIcon
                          className={cn(
                            'h-4 w-4 transition-transform duration-200',
                            isActive && 'rotate-180 text-brand-700',
                          )}
                        />
                      </span>
                    </button>

                    {isActive && (
                      <div className="pl-3 py-1.5 flex flex-col gap-2 border-l border-brand-200 bg-brand-50/30 rounded-r-md">
                        {group.subGroups.map((sub) => (
                          <div key={sub.subCategory} className="flex flex-col gap-1 mt-1">
                            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
                              {sub.subCategory}
                            </span>
                            {sub.items.map((item) => (
                              <Link
                                key={item.id}
                                to={`/client/services/${item.serviceId}`}
                                onClick={onClose}
                                className="py-1 text-xs font-medium text-gray-700 hover:text-brand-700 transition-colors"
                              >
                                {item.name}
                              </Link>
                            ))}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <Link
            to="/client/services"
            onClick={onClose}
            className="py-2 text-sm font-semibold text-brand-700 hover:underline flex items-center gap-1"
          >
            Browse all services →
          </Link>
        </div>
      )}
    </div>
  );
}

export function AppHeader() {
  const [mobileOpen, setMobileOpen] = useState(false);

  function handleNavClick(e: React.MouseEvent<HTMLAnchorElement>, href: string) {
    if (href.startsWith('#')) {
      e.preventDefault();
      setMobileOpen(false);

      const targetId = href.substring(1);
      const el = document.getElementById(targetId);

      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
        window.history.pushState(null, '', href);
      } else {
        window.location.href = '/' + href;
      }
    }
  }

  function handleLogoClick(e: React.MouseEvent) {
    setMobileOpen(false);
    if (window.location.pathname === '/') {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
      window.history.pushState(null, '', '/');
    }
  }

  return (
    <header className="sticky top-0 z-50 border-b border-gray-200/80 bg-white/95 backdrop-blur-md w-full">
      <div className="mx-auto flex w-full max-w-7xl 2xl:max-w-[1400px] items-center justify-between px-4 sm:px-6 lg:px-8 py-3">
        <Link to="/" className="flex items-center gap-2" onClick={handleLogoClick}>
          <img src="/ecregi_logo.png" alt="EcRegi" className="h-8 w-8" />
          <span className="text-lg font-bold text-brand-700 tracking-tight">ECREGI</span>
        </Link>

        <nav className="hidden items-center gap-6 md:flex">
          <ServicesMenu />
          {NAV.map((item) => (
            <a
              key={item.href}
              href={item.href}
              onClick={(e) => handleNavClick(e, item.href)}
              className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
            >
              {item.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2.5">
          {/* Heart outline icon from reference image */}
          <button
            type="button"
            className="p-1.5 text-gray-500 hover:text-brand-700 transition-colors hidden sm:flex items-center justify-center rounded-md hover:bg-gray-100"
            title="Saved"
          >
            <svg className="w-4.5 h-4.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.8}
                d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.684a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
              />
            </svg>
          </button>

          <Link to="/associate/login" className="hidden sm:inline-flex">
            <Button
              variant="outline"
              className="border-gray-300 text-gray-700 hover:bg-gray-50 text-xs sm:text-sm font-semibold px-3.5 py-1.5 rounded-lg shadow-2xs"
            >
              Associate login
            </Button>
          </Link>
          <Link to="/login" onClick={() => setMobileOpen(false)}>
            <Button className="bg-brand-700 hover:bg-brand-800 text-white font-semibold text-xs sm:text-sm px-4 py-1.5 rounded-lg shadow-xs">
              Client login
            </Button>
          </Link>

          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            className="rounded-lg p-2 text-gray-600 hover:bg-gray-100 hover:text-gray-900 md:hidden"
            aria-label="Toggle Menu"
          >
            {mobileOpen ? <XIcon className="h-5 w-5" /> : <MenuIcon className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile Drawer */}
      {mobileOpen && (
        <div className="absolute left-0 right-0 top-full z-10 border-b border-gray-200 bg-white px-6 py-6 shadow-lg backdrop-blur-md md:hidden max-h-[85vh] overflow-y-auto">
          <div className="flex flex-col gap-4">
            <MobileServicesMenu onClose={() => setMobileOpen(false)} />

            {NAV.map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={(e) => handleNavClick(e, item.href)}
                className="py-2 text-base font-semibold text-gray-800 hover:text-gray-900"
              >
                {item.label}
              </a>
            ))}

            <div className="my-2 border-t border-gray-100" />

            <Link
              to="/associate/login"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-2 py-2 text-base font-semibold text-gray-800 hover:text-gray-900"
            >
              <ShieldIcon className="h-5 w-5 text-brand-700" />
              Associate login
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
